/**
 * @Description 高德地图相关方法
 */

import { isArray, isObject } from '@lhb/func';
import { createRoot } from 'react-dom/client';
import { v4 } from 'uuid';
import { areaList } from '../components/config-v2';
import { isApp, isWeComEnv } from './tools';
import { locationLngLat } from './bridge';
import { postAmapLocation } from '@/views/spotevaluation/api/spotevaluation';
import { Toast } from 'antd-mobile';
import { initWxWorkAndGetLocation } from './wx';

// 重庆市下的重庆市区code
export const CQ_CODE_CITY = '500100';
// 重庆市下的重庆郊县code
export const CQ_CODE_SUBURB = '500200';
// 市级下各区域的行政区围栏颜色
export const districtColor = [
  'rgba(146, 240, 201, 0.8)',
  'rgba(189, 226, 159, 1)',
  'rgba(136, 255, 174, 1)',
  'rgba(249, 133, 87, 1)',
  'rgba(107, 250, 255, 1)',
  'rgba(255, 183, 200, 1)',
  'rgba(78, 176, 255, 1)',
  'rgba(0, 255, 159, 1)',
  'rgba(255, 225, 192, 1)'
];

// 中国的四个直辖市和特别行政区（包含台湾）
export const MUNICIPALITY_AND_SAR = [
  '北京',
  '天津',
  '上海',
  '重庆',
  '香港',
  '澳门',
  '台湾'
];

// 重庆市下的重庆郊县name
export const CQ_SUBURB_NAME = '重庆郊县';

// https://lbs.amap.com/api/javascript-api-v2/guide/abc/load 永国配的服务端转发
// export const aMapServiceHost = 'https://location.locxx.com/_AMapService2024';
// 因为高德地图未获得商用授权，所以需要经常换
// export const aMapServiceHost = 'https://location.locxx.com/_AMapService20240808';
// 商用版！！！
// export const aMapServiceHost = 'https://location.locxx.com/_AMapService20240904';
export const aMapServiceHost = process.env.AMAP_SERVICE_HOST;

export const auditAccountMobile = '13512345678';

// 地图背景色枚举值
export const MapThemeEnum = {
  NORMAL: 'normal', // 标准
  // DARK: 'dark', // 幻影黑
  // LIGHT: 'light', // 月光银
  WHITESMOKE: 'whitesmoke', // 远山黛
  SATELLITE: 'satellite', // 卫星
};

/**
 * 注意使用该方法前，需要初始化高德地图
 * 逆地理编码(经纬度转换成具体地址)
 * @param {Array} lnglat 经纬度
 * @param {String} cityName 城市名
 * @param {Boolean} onlyAddress 是否仅返回具体地址
 */
export function getLngLatAddress(lnglat, cityName = '全国', onlyAddress = true) {
  const geocoder = new window.AMap.Geocoder({
    city: cityName // 默认：“全国”
  });

  return new Promise((resolve, reject) => {
    geocoder.getAddress(lnglat, (status, result) => {
      if (status === 'complete' && result.regeocode) {
        const { regeocode } = result;
        if (onlyAddress) {
          const { formattedAddress } = regeocode || {};
          resolve(formattedAddress);
        } else {
          resolve(regeocode);
        }
      } else {
        reject(new Error(`根据经纬度查询地址失败`));
      }
    });
  });
}


/**
 * 绘制市级下面区的行政区围栏
 * 注意使用该方法前，需要引入'AMap.DistrictSearch'插件
 *
 */
export function drawDistrictPath(mapIns: any, cityName: string, colors = districtColor, options = {}) {
  if (!mapIns || !cityName) return;
  const district = new window.AMap.DistrictSearch();
  // 目前北京、上海、天津、重庆四个直辖市需要特殊处理，如果不处理，搜索北京和北京市的结果不一样
  let name = cityName;
  if (['北京', '上海', '重庆', '天津'].find((item: any) => name.indexOf(item) !== -1)) {
    name = name.replace('市', '');
  }
  district.search(name, (status, result): any => {
    if (status === 'complete') {
      const { districtList } = result;
      if (!(isArray(districtList) && districtList.length)) return;
      const cityLevel = districtList.filter((item: any) => item.level === 'city');
      if (!cityLevel?.length) return false;
      const adcodes: string[] = [];
      cityLevel.forEach((item: any) => {
        const { adcode } = item;
        adcode && adcodes.push(adcode);
      });
      let colorIndex = 0;
      const districtLayer = new window.AMap.DistrictLayer.Province({
        zIndex: 10,
        // 行政区的编码 adcode与省市行政区对照表
        adcode: adcodes,
        // depth设定数据的层级深度，depth为0的时候只显示国家面，depth为1的时候显示省级， 当国家为中国时设置depth为2的可以显示市一级
        depth: 2,
        opacity: 0.5,
        styles: {
          'fill': () => {
            let color = '';
            if (colorIndex > colors.length - 1) {
              colorIndex = 0;
            }
            color = colors[colorIndex];
            colorIndex++;
            return color;
          },
          'stroke-width': 4,
          'province-stroke': '#0051C2',
          // 'city-stroke': '#f23030', // 中国地级市边界
          'city-stroke': '#fff', // 中国地级市边界
          'county-stroke': '#fff' // 中国区县边界
        },
        ...options
      });
      districtLayer.setMap(mapIns);
    }
  });
}


/**
 * 上面的drawDistrictPath和pc端的有差异，先直接复制pc端的，等有空了，组内前端在想想怎么兼容其他使用 drawDistrictPath的地方
 */
export function drawDistrictPath2(mapIns: any, cityName: string, colors = districtColor, options = {}) {
  if (!mapIns || !cityName) return;
  let districtLayer:any;
  const district = new window.AMap.DistrictSearch();
  // 目前北京、上海、天津、重庆四个直辖市需要特殊处理，如果不处理，搜索北京和北京市的结果不一样
  let name = cityName;
  if (MUNICIPALITY_AND_SAR.find((item: any) => name.indexOf(item) !== -1)) {
    name = name.replace('市', '');
  }

  return new Promise((resolve, reject) => {
    district.search(name, (status, result): any => {
      if (status === 'complete') {
        const { districtList } = result;
        if (!(isArray(districtList) && districtList.length)) return;
        const cityLevel = districtList.filter((item: any) => item.level === 'city');
        if (!cityLevel?.length) return false;
        const adcodes: string[] = [];
        cityLevel.forEach((item: any) => {
          const { adcode } = item;
          if ((options as any)?.customOpenCQCheck && adcode === CQ_CODE_SUBURB) return;
          adcode && adcodes.push(adcode);
        });
        let colorIndex = 0;
        districtLayer = new window.AMap.DistrictLayer.Province({
          zIndex: 10,
          // 行政区的编码 adcode与省市行政区对照表
          adcode: adcodes,
          // depth设定数据的层级深度，depth为0的时候只显示国家面，depth为1的时候显示省级， 当国家为中国时设置depth为2的可以显示市一级
          depth: 2,
          opacity: 0.5,
          styles: {
            'fill': () => {
              let color = '';
              if (colorIndex > colors.length - 1) {
                colorIndex = 0;
              }
              color = colors[colorIndex];
              colorIndex++;
              return color;
            },
            'stroke-width': 4,
            'province-stroke': '#0051C2',
            // 'city-stroke': '#f23030', // 中国地级市边界
            'city-stroke': '#fff', // 中国地级市边界
            'county-stroke': '#fff' // 中国区县边界
          },
          ...options
        });
        districtLayer.setMap(mapIns);
        return resolve(districtLayer);
      }
      return reject(status);
    });
  });
}

/**
 * 废弃，使用getCurPosition
 * 注意使用该方法前要引入对应的plugins
  * 浏览器精确定位 https://lbs.amap.com/demo/jsapi-v2/example/location/browser-location
  * 浏览器定位失败常见原因：https://lbs.amap.com/faq/js-api/map-js-api/position-related/43361
  * @param {*} options 自定义参数
  */
export function browserPosition (options: any) {
  return new Promise((resolve, reject) => {
    window.AMap.plugin('AMap.Geolocation', () => {
      const geolocation = new window.AMap.Geolocation({
        enableHighAccuracy: options.enableHighAccuracy || true, // 是否使用高精度定位，默认:true
        timeout: options.timeout, // 默认5s
        // buttonPosition: options.buttonPosition || 'RB', // 定位按钮的停靠位置
        // buttonOffset: options.buttonOffset || new window.AMap.Pixel(10, 20), // 定位按钮与设置的停靠位置的偏移量，默认：Pixel(10, 20)
        zoomToAccuracy: true // 定位成功后是否自动调整地图视野到定位点
      });

      geolocation.getCurrentPosition((status, result) => {
        if (status === 'complete') {
          Object.assign(result, {
            customRecognitionType: 1 // 自定义类别
          });
          return resolve(result); // 获取定位成功，返回定位信息
        }

        // 精确定位失败时获取城市定位信息
        if (options.isCompatibility) {
          return getCurCityPosition(resolve, reject, geolocation);
        } else {
          return reject(result);
        }
      });
    });
  });
};
// 获取当前浏览器定位到的城市信息
export function getCurCityPosition(resolveFn, rejectFn, geolocation) {
  geolocation.getCityInfo((status, result) => {
    if (status === 'complete') {
      Object.assign(result, {
        customRecognitionType: 2 // 自定义类别
      });
      return resolveFn(result); // 返回当前城市的基础信息
    } else {
      return rejectFn(result);
    }
  });
};


/**
 * 创建浏览器定位实例
 * @param {Object} options 创建浏览器定位实例的相关参数
 * https://lbs.amap.com/api/jsapi-v2/documentation#geolocation
 * @returns 浏览器定位实例
 */
function getGeolocationInstance(options: any) {
  const AMap = window.AMap;
  return new AMap.Geolocation({
    enableHighAccuracy: true, // 是否使用高精度定位，默认:true
    timeout: 1000, // 默认100ms
    position: 'RB', // 定位按钮的停靠位置
    offset: new AMap.Pixel(10, 20), // 定位按钮与设置的停靠位置的偏移量，默认：Pixel(10, 20)
    // noIpLocate: 3, // 模拟定位失败时可设置此参数
    // noGeoLocation: 3, // 模拟定位失败时可设置此参数
    zoomToAccuracy: true, // 定位成功后是否自动调整地图视野到定位点
    getCityWhenFail: true, // 定位失败之后是否返回基本城市定位信息
    needAddress: true, // 是否需要将定位结果进行逆地理编码操作
    useNative: true, // 是否与高德定位SDK能力结合，需要同时使用安卓版高德定位sdk，否则无效
    ...options
  });
}

/**
 * 浏览器精确定位
 * 文档：https://lbs.amap.com/demo/jsapi-v2/example/location/browser-location
 * 浏览器定位失败常见原因：https://lbs.amap.com/faq/js-api/map-js-api/position-related/43361
 * @param {*} map // 地图实例
 * @param {*} options 自定义参数
 */
export function getCurPosition(map: any, options: any) {
  const AMap = window.AMap;
  if (!AMap) {
    return Promise.resolve({});
  }
  return new Promise((resolve, reject): void => {
    AMap.plugin('AMap.Geolocation', () => {
      const geolocation = getGeolocationInstance(options);
      // map.addControl(geolocation);
      geolocation.getCurrentPosition((status: string, result: any) => {
        if (status === 'complete') { // 获取定位成功，返回定位信息
          return resolve(result);
        }

        reject(result);
      });
    });
  });
}

/**
 * 通过高德地图获取到的城市名，特殊场景：直辖市、直辖县
 * @param data 通过高德获取到的当前地图中心点的省市区信息
 */
export function getAMapCityName(data: any) {
  const { city, province, district } = data || {};
  // 有城市名时
  if (city) return city;
  // 地图定位在重庆，重庆作为直辖市（相当于省级行政单位），分为重庆市（相当于市级行政单位）、重庆郊县（相当于市级行政单位），重庆郊县下的区级数据共通的规则是'xxx县'
  if (province?.includes('重庆')) {
    // 如果当前district是重庆下的县时
    if (district && district.substring(district.length - 1) === '县') return CQ_SUBURB_NAME;
    return province;
  }
  // 直辖市时
  if (MUNICIPALITY_AND_SAR.find((item: any) => province?.includes(item))) return province;
  // 省辖县（全国有15个）
  return district;
}

/**
 * @params marker 需要展示内容的marker
 * @params content 需要展示的内容
 */
export async function showMarker(marker, content) {
  const uid = v4();
  // marker.setContent(`<div id="${uid}"></div>`);
  // createRoot(document.getElementById(uid)!).render(content);
  marker.setContent(`<div id="${uid}"></div>`);
  const extData = marker.getExtData();
  if (isObject(extData)) {
    marker.setExtData({ ...extData, uid });
  } else {
    marker.setExtData({ uid });
  }
  const targetDom = document.getElementById(uid);
  targetDom && createRoot(targetDom!).render(content);
}

/**
 *
 * @param arr 围栏数据
 * @param ways 判断的方法，入参为coord, index, curArr,返回true或者false
 * @returns 返回最东、西、南、北边（根据传入的way)的经纬度
 */
export async function getAroundLngLat(arr, way) {
  return new Promise((resolve, reject) => {
    if (!isArray(arr))reject(`${arr} is not a Array`);
    // 寻找最小纬度值和对应的经度值
    let lng, lat;
    // 数据可能是多维的，所以直接打平
    // [lng1,lat1,lng2,lat2,lng3,lat3]
    arr.flat(Infinity).forEach((coord, index, curArr) => {
      if (index === 0) return;
      if (index === 1) {
        lng = curArr[index - 1];
        lat = coord;
        return;
      }
      // if (index % 2 === 1 && coord > minLat) -- maxLat->最北边（上）
      // if (index % 2 === 1 && coord < minLat) -- maxLat->最南边（下）
      // if (index % 2 === 0 && coord > minLng) -- maxLat->最东边（右）
      // if (index % 2 === 0 && coord < minLng) -- maxLat->最西边（左）

      // 比较lat数据
      if (way(coord, index, curArr, lng, lat)) {
        lng = curArr[index - 1];
        lat = coord;
      }
    });
    resolve([lng, lat]);
  });
}
/**
* 获取行政区域范围
* TODO待优化，没有针对amapOptions做具体的逻辑区分，amapOptions内参数值不同，返回的结构不同，使用时要注意
* @param {Object} amapOptions 地图查询参数 https://lbs.amap.com/demo/jsapi-v2/example/district-search/draw-district-boundaries
* @param {String} name | adcode  行政区域名称（或者adcode，推荐使用adcode，区名称可能存在重复）
* @param {Boolean} onlyBounds 是否仅返回范围参数
* @return {Array} 对应的行政区域数组
*/
export function getDistrictBounds(amapOptions, name, onlyBounds = true) {
  const district = new window.AMap.DistrictSearch(amapOptions);
  return new Promise((resolve, reject) => {
    district.search(name, (status, result) => {
      if (status === 'complete' && result && isArray(result.districtList)) {
        const { districtList } = result;
        if (onlyBounds) { // 仅返回查询的行政区域的范围，
          const bounds = districtList[0].boundaries;
          resolve(bounds);
        } else { // 返回高德地图的原始数据
          resolve(districtList);
        }
      } else {
        reject(status);
      }
    });
  });
}

export const getCityForAMapData = (data: any[]) => {
  const _cityForAMap: any[] = [];
  // 禁选特殊的行政区
  data?.forEach((item: any) => {
    if (['台湾', '香港', '澳门'].some(region => item.name.indexOf(region) !== -1)) {
      item.children?.forEach(city => {
        city.disabled = true;
        city.children?.forEach(district => {
          district.disabled = true;
        });
        // cityForAMap数据组装
        _cityForAMap.push({
          name: city.name,
          id: city.id,
          code: city.code,
          provinceName: item.name,
          provinceId: item.id,
          provinceCode: item.code,
          district: city.children,
          disabled: true
        });
      });
      return {
        ...item,
        disabled: true
      };
    }
    // cityForAMap数据组装
    item.children?.forEach(city => {
      _cityForAMap.push({
        name: city.name,
        id: city.id,
        code: city.code, // 对应高德数据中城市的adcode
        provinceName: item.name,
        provinceId: item.id,
        provinceCode: item.code, // 对应高德数据中省份的adcode
        district: city.children
      });
    });
    return item;
  });
  return _cityForAMap;
};

/**
 * 根据经纬度返回省市区id
 * @param {Array|Object} lnglat 经纬度
 * @return {Object} 对应的省市区id和name和具体位置信息
 */
export function getPCDInfo(
  lnglat,
  mapIns
) {
  if (!mapIns) return;
  return new Promise(async (resolve, reject) => {
    const addressInfo: any = await getLngLatAddress(lnglat, '', false);
    if (!addressInfo) {
      reject(new Error(`根据经纬度查询地址失败`));
      return;
    };
    // console.log(`addressInfo`, addressInfo);
    const { addressComponent, formattedAddress } = addressInfo;
    const { district } = addressComponent || {};
    const cityName = getAMapCityName(addressComponent);
    const pcdInfo: any = {
      address: formattedAddress
    };
    const cityResult: any = await areaList({ type: 1 });
    const cityForAMap = getCityForAMapData(cityResult);
    // console.log(`cityForAMap`, cityForAMap);
    const cityInfo = cityForAMap?.find(item => item.name.indexOf(cityName) > -1);
    // console.log(`cityInfo`, cityInfo);
    const { district: districtList, id: cityId, provinceId, provinceName } = cityInfo || {};
    pcdInfo.provinceId = provinceId;
    pcdInfo.provinceName = provinceName;
    pcdInfo.cityId = cityId;
    pcdInfo.cityName = cityName;
    const targetDistrict = districtList?.find((districtItem: any) => districtItem?.name?.includes(district));
    if (targetDistrict) {
      const { id: districtId, name: districtName } = targetDistrict;
      pcdInfo.districtId = districtId;
      pcdInfo.districtName = districtName;
    }
    resolve(pcdInfo);
  });
}


/**
 * 地图添加图片形式的自定义Marker
 * @param { Object } 相关配置
 */
export function addImageMarker({
  lng, // 经度
  lat, // 纬度
  width, // 图片宽,
  height, // 图片高
  imageUrl = `${process.env.STATIC_RES_DOMAIN}/project-custom/locationpc/icon_blue_marker_solid@2x.png`,
  // https://lbs.amap.com/api/javascript-api-v2/documentation#icon
  iconConfig = {}, // 外部传入的自定义图片配置
  // https://lbs.amap.com/api/javascript-api-v2/documentation#marker
  markerConfig = {}, // 外部传入的自定义Marker配置
}) {
  if (!window.AMap) {
    throw new Error('高德地图未挂载');
  }

  const AMap = window.AMap;
  const customIcon = new AMap.Icon({
    // 图标尺寸
    size: new AMap.Size(width, height),
    image: imageUrl,
    // 图标所用图片大小
    imageSize: new AMap.Size(width, height),
    // 图标取图偏移量
    // imageOffset: new AMap.Pixel(0, 10)
    ...(isObject(iconConfig) ? iconConfig : {}),
  });
  return new AMap.Marker({
    position: new AMap.LngLat(lng, lat),
    // 将一张图片的地址设置为 icon
    icon: customIcon,
    // 设置了 icon 以后，设置 icon 的偏移量，以 icon 的 [center bottom] 为原点
    offset: new AMap.Pixel(-(width / 2), -height),
    ...(isObject(markerConfig) ? markerConfig : {}),
  });
}

/**
 * 注意使用该方法前，需要先实例化高德地图
 * 经纬度和省份名称任选其一传入
 * @param {String} provinceName 省份名
 * @param {Array} lnglat 经纬度
 * @param {string} level 查询等级，province为省份，city为城市,district:区/县
 * @param {any} config DistrictSearch相关配置，可参照 https://lbs.amap.com/api/javascript-api-v2/documentation#districtsearch
 * @returns {Object} 返回省份信息（adcode、省份名、下属市区）
 */
export async function getProvinceAdcode(provinceName?:string, lnglat?:any, level:string = 'province', config?:any) {
  // 创建AMap.DistrictSearch对象
  const districtSearch = new window.AMap.DistrictSearch({
    subdistrict: 1, // 返回下一级行政区
    level: 'province', // 查询省级行政区
    ...config
  });

  // 通过经纬度获取省份名称
  const res:any = await getLngLatAddress(lnglat, '', false);
  const { addressComponent } = res || {};
  if (!addressComponent && !provinceName) throw new Error('获取数据失败');
  const { province, city, district } = addressComponent || {};
  if (!province && !provinceName) throw new Error('获取数据失败');

  // 使用AMap.DistrictSearch进行查询
  let name;
  if (level === 'province') {
    name = provinceName || province;
  } else if (level === 'city') {
    // 当处在直辖市的状态下，city为null，取province
    name = city || province;
  } else if (level === 'district') {
    name = district || city || province;
  }

  return new Promise((resolve, reject) => {
    districtSearch.search(name, (status, result) => {
      if (status === 'complete' && result) {
        if (result.districtList && result.districtList.length > 0) {
          const province = result.districtList[0];
          if (province) return resolve(province);// 输出省份信息
        }
      }
      return reject(status);
    });
  });
}



export interface LocationProps {
  latitude?: number;
  longitude?: number;
  /** 定位精度，单位：米 */
  accuracy?: number;
  /** 定位名称 */
  POIName?: string;
  /** 定位地址 */
  address?: string;
  provinceId?: string;
  cityId?: string;
  districtId?: string;
  provinceName?: string;
  cityName?: string;
  districtName?: string;
  cityCode?: string;
  districtCode?: string;
  /** 定位平台：h5/app/wxwork */
  platform?: string;
  /** 不同平台下返回的其他字段 */
  [key: string]: any;
}

interface LocationLnglatProps {
  latitude?: number;
  longitude?: number;
  /** 定位精度，单位：米 */
  accuracy?: number;
  /** 定位地址 */
  address?: string,
}

/**
 * app 内获取当前位置经纬度
 * @description app 获取定位的限制：同时多次请求只会获取一次，其他的请求会无响应，所以最好不要同时多次获取定位
 */
const getAppLocationLnglat = async (): Promise<LocationLnglatProps> => {
  let location:LocationLnglatProps = {};

  try {
    // APP环境：使用原生定位
    const _location: any = await locationLngLat({
      openSystemPermission: 0 // 判断是否有权限
    }) || {};

    // 转换为标准格式
    location = {
      ...(_location || {}),
      address: _location.address || '',
      latitude: _location.lat,
      longitude: _location.lng,
      accuracy: _location.accuracy,
    };

  } catch (error: any) {
    console.error('app 获取位置失败:', error);
    // 返回一个默认的位置对象或抛出自定义错误
    Toast.show(error?.message || '获取位置失败');
    return {}; // 或者 throw new Error('获取位置失败');
  }

  return location;
};

/** 企微内获取当前位置经纬度 */
const getWxWorkLocationLnglat = async ({ mapIns }: {
  /** 地图实例，如果企微定位失败，就用 h5 定位 */
  mapIns?: any;
}): Promise<LocationLnglatProps> => {
  let location:LocationLnglatProps = {};

  try {
    // 企微环境：使用企微SDK定位
    const _location: any = await initWxWorkAndGetLocation({ mapIns }).catch(() => {}) || {};

    // 转换为标准格式
    location = {
      ...(_location || {}),
      longitude: _location.longitude,
      latitude: _location.latitude,
      accuracy: _location.accuracy,
    };
  } catch (error: any) {
    console.error('企微获取位置失败:', error);
    Toast.show(error?.message || '获取位置失败');
    return {};
  }

  return location;
};

/** h5 内获取当前位置经纬度 */
const getH5LocationLnglat = async ({ mapIns, mapConfig = {} }: {
  /** 地图实例，企微定位失败时，会用 h5 定位 */
  mapIns?: any;
  /** 地图配置项 */
  mapConfig?: any;
}): Promise<LocationLnglatProps> => {

  let location:LocationLnglatProps = {};

  try {
    // 浏览器精确定位
    const _location: any = await getCurPosition(mapIns, {
      // https://lbs.amap.com/api/javascript-api-v2/documentation#geolocation
      // 是否显示定位按钮
      showButton: false,
      showCircle: false,
      showMarker: false,
      ...(mapConfig || {}),
    });

    // 转换为标准格式
    location = {
      ...(_location || {}),
      longitude: _location.position?.lng || _location.position?.longitude || _location.position?.[0],
      latitude: _location.position?.lat || _location.position?.latitude || _location.position?.[1],
      accuracy: _location.accuracy,
    };
  } catch (error: any) {
    console.error('H5 获取位置失败:', error);
    Toast.show(error?.message || '获取位置失败');
    return {};
  }

  return location;
};

/**
 * 获取当前定位位置信息（所有平台：app、h5、企微）
 * @description app 获取定位的限制：同时多次请求只会获取一次，其他的请求会无响应，所以最好不要同时多次获取定位
 * @demo
    async getLocation() {
      const _location = await getLocationAllPlatform({ mapIns, mapConfig });
      setLocation(_location);
    },
 */
export const getLocationAllPlatform = async ({ mapIns, mapConfig }: {
  /** 地图实例，企微定位失败时，会用 h5 定位 */
  mapIns?: any;
  /** 地图配置项 */
  mapConfig?: any;
} = {}): Promise<LocationProps> => {

  let location:any = {};
  let platform = 'h5';

  // 先判断是否是APP环境
  if (isApp()) {
    platform = 'app';
    location = await getAppLocationLnglat().catch(() => {});
  } else if (isWeComEnv()) {
    platform = 'wxwork';
    location = await getWxWorkLocationLnglat({ mapIns }).catch(() => {});
  } else {
    platform = 'h5';
    location = await getH5LocationLnglat({ mapIns, mapConfig }).catch(() => {});
  }

  // 如果定位失败，尝试使用H5定位
  if (!location.longitude || !location.latitude) {
    platform = 'h5';
    location = await getH5LocationLnglat({ mapIns, mapConfig }).catch(() => {});
  }

  if (location.longitude && location.latitude) {
    location.platform = platform;

    // 使用接口获取省市区信息
    try {
      await Promise.all([
        getLngLatAddress([location.longitude, location.latitude]),
        postAmapLocation({ lng: location.longitude, lat: location.latitude })
      ]).then(async ([address, locationRes]) => {
        // 处理可能的 data 字段
        const addressInfo = locationRes?.data || locationRes;
        if (addressInfo) {
          Object.assign(location, {
            POIName: addressInfo.poiName || '',
            address: address || addressInfo.address || location.address || '',
            provinceId: addressInfo.provinceId,
            cityId: addressInfo.cityId,
            districtId: addressInfo.districtId,
            provinceName: addressInfo.provinceName,
            cityName: addressInfo.cityName,
            districtName: addressInfo.districtName,
            cityCode: addressInfo.cityCode,
            districtCode: addressInfo.districtCode,
            lng: location.longitude,
            lat: location.latitude,
          });
        }
      });
    } catch (addressError) {
      console.error('获取地址信息失败:', addressError);
    }
  }

  return location;
};

